import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export interface QueuedLabel {
  code: string;
  title: string;
  subtitle?: string;
  location?: string;
  type: 'tool' | 'location';
  id: string; // Original tool/location ID
}

interface LabelQueueContextType {
  labels: QueuedLabel[];
  addLabel: (label: QueuedLabel) => void;
  removeLabel: (code: string) => void;
  clearQueue: () => void;
}

const QUEUE_STORAGE_KEY = 'toolcrib_label_queue';

const LabelQueueContext = createContext<LabelQueueContextType | undefined>(undefined);

// Load from localStorage
function loadFromStorage<T>(key: string, defaultValue: T): T {
  try {
    const stored = localStorage.getItem(key);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch (e) {
    console.error(`Failed to load ${key} from localStorage:`, e);
  }
  return defaultValue;
}

// Save to localStorage
function saveToStorage<T>(key: string, value: T): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    console.error(`Failed to save ${key} to localStorage:`, e);
  }
}

export function LabelQueueProvider({ children }: { children: ReactNode }) {
  const [labels, setLabels] = useState<QueuedLabel[]>(() =>
    loadFromStorage<QueuedLabel[]>(QUEUE_STORAGE_KEY, [])
  );

  // Persist queue to localStorage whenever it changes
  useEffect(() => {
    saveToStorage(QUEUE_STORAGE_KEY, labels);
  }, [labels]);

  const addLabel = (label: QueuedLabel) => {
    setLabels(prev => {
      // Avoid duplicates in queue
      if (prev.some(l => l.code === label.code)) {
        return prev;
      }
      return [...prev, label];
    });
  };

  const removeLabel = (code: string) => {
    setLabels(prev => prev.filter(l => l.code !== code));
  };

  const clearQueue = () => {
    setLabels([]);
  };

  return (
    <LabelQueueContext.Provider value={{
      labels,
      addLabel,
      removeLabel,
      clearQueue,
    }}>
      {children}
    </LabelQueueContext.Provider>
  );
}

export function useLabelQueue() {
  const context = useContext(LabelQueueContext);
  if (!context) {
    throw new Error('useLabelQueue must be used within LabelQueueProvider');
  }
  return context;
}
